import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { imageSetAvatarThunk } from "../image/imageSetAvatar";
import { ObjectTarget } from "../../app/permissions";

export interface SchoolState {
    state: LoadStateInfo;
    selected?: SchoolInfo;
}

const initialState: SchoolState = {
    state: notStarted(),
};

export interface RetrieveSchoolRequest {
    schoolId?: string;
}

export const retrieveSchool = createAsyncThunk(
    "retrieveSchool",
    async (param: RetrieveSchoolRequest, thunkAPI) => {
        console.debug("retrieve school");

        const response = await fetch(`${getBackendUrl()}/api/school/${param.schoolId}`, {
            signal: thunkAPI.signal,
            credentials: "include",
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSchoolRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.school;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            return false;
        }
    }
);


export const schoolSlice = createSlice({
    name: "school",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<string>) => {
            if (state.selected?.id !== action.payload) {
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        select: (state, action: PayloadAction<SchoolInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.name}`);
            state.state = loaded();
            state.selected = action.payload;
        },
        clean: (state) => {
            state.state = notStarted();
            state.selected = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSchool.pending, (state, /* action */) => {
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveSchool.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveSchool.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(imageSetAvatarThunk.fulfilled, (state, action) => {
                if (state.selected
                    && action.meta.arg.objectTarget == ObjectTarget.SCHOOL
                    && action.meta.arg.targetId == state.selected?.id
                ) {
                    state.selected.url = action.payload;
                }
            });
    },
});

export interface SchoolInfo {
    id: string;
    name: string;
    url?: string;
    imageProcessorStyle?: number;
}

// export const {} = schoolSlice.actions;

export const selectSchool = (state: RootState) => state.school;

export default schoolSlice.reducer;
